// 학부모 교시 스케줄 등록 페이지 (드래그 선택 지원)

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];

interface PeriodTemplate {
  period_number: number;
  period_name: string;
  period_type: string;
  start_time: string;
  end_time: string;
  display_order: number;
  is_active: boolean;
}

interface Child {
  id: string;
  name: string;
  organizationName: string;
  scheduleType?: string;
}

export default function PeriodSchedulePage() {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { parent } = useSupabaseParentAuth();

  const [children, setChildren] = useState<Child[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string>(childId || '');
  const [templates, setTemplates] = useState<PeriodTemplate[]>([]);
  const [schedules, setSchedules] = useState<Record<string, number[]>>({});
  const [savedSchedules, setSavedSchedules] = useState<Record<string, number[]>>({});
  const [modifierInfo, setModifierInfo] = useState<{ modifier_role: string; updated_at: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 드래그 상태
  const isDragging = useRef(false);
  const dragMode = useRef<'add' | 'remove'>('add');

  useEffect(() => {
    if (parent?.id) loadChildren();
  }, [parent?.id]);

  useEffect(() => {
    if (selectedChildId) {
      // 주간 스케줄 조직이면 주간 스케줄 페이지로 이동
      const child = children.find(c => c.id === selectedChildId);
      if (child?.scheduleType === 'weekly') {
        navigate(`/parent/schedule/${selectedChildId}`, { replace: true });
        return;
      }
      loadTemplates();
      loadSchedules();
    }
  }, [selectedChildId, children]);

  // 드래그 종료 이벤트
  useEffect(() => {
    const stopDrag = () => { isDragging.current = false; };
    document.addEventListener('mouseup', stopDrag);
    document.addEventListener('touchend', stopDrag);
    return () => {
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchend', stopDrag);
    };
  }, []);

  // 터치 드래그 핸들러
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement;
    if (!el) return;
    const target = el.closest('[data-cell]') as HTMLElement;
    if (!target?.dataset.cell) return;
    const [day, period] = target.dataset.cell.split('-').map(Number);
    applyDrag(day, period);
  }, []);

  const loadChildren = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const res = await fetch('/api/v1/parent/children', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;

      const data = await res.json();
      const list: Child[] = data.map((c: any) => ({
        id: c.id,
        name: c.name || '이름 없음',
        organizationName: c.organization_name || '독서실',
        scheduleType: c.schedule_type || 'weekly',
      }));
      setChildren(list);
      if (list.length > 0 && !selectedChildId) setSelectedChildId(list[0].id);
    } catch (err: any) {
      console.error('자녀 조회 에러:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const res = await fetch(`/api/v1/parent/children/${selectedChildId}/period-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;

      const data = await res.json();
      setTemplates(data.data || []);
    } catch (err: any) {
      console.error('교시 시간표 조회 에러:', err);
    }
  };

  const loadSchedules = async () => {
    setIsLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) return;

      const res = await fetch(`/api/v1/parent/children/${selectedChildId}/period-schedules`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const scheduleData = data.data || {};
        setSchedules(scheduleData);
        setSavedSchedules(JSON.parse(JSON.stringify(scheduleData)));
        setModifierInfo(data.modifier_info || null);
      } else {
        setSchedules({});
        setSavedSchedules({});
        setModifierInfo(null);
      }
    } catch (err: any) {
      console.error('교시 스케줄 조회 에러:', err);
      setSchedules({});
      setSavedSchedules({});
    } finally {
      setIsLoading(false);
    }
  };

  const applyDrag = (day: number, period: number) => {
    setSchedules((prev) => {
      const dayKey = String(day);
      const current = prev[dayKey] || [];
      if (dragMode.current === 'add' && !current.includes(period)) {
        return { ...prev, [dayKey]: [...current, period].sort((a, b) => a - b) };
      } else if (dragMode.current === 'remove' && current.includes(period)) {
        return { ...prev, [dayKey]: current.filter((p) => p !== period) };
      }
      return prev;
    });
  };

  const startDrag = (day: number, period: number) => {
    isDragging.current = true;
    const isChecked = (schedules[String(day)] || []).includes(period);
    dragMode.current = isChecked ? 'remove' : 'add';
    applyDrag(day, period);
  };

  const continueDrag = (day: number, period: number) => {
    if (!isDragging.current) return;
    applyDrag(day, period);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('로그인이 필요합니다.');

      const res = await fetch(`/api/v1/parent/children/${selectedChildId}/period-schedules`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ schedules }),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || '저장 실패');
      }

      setSavedSchedules(JSON.parse(JSON.stringify(schedules)));
      setModifierInfo({ modifier_role: 'parent', updated_at: new Date().toISOString() });
      setSuccess('교시 스케줄이 저장되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const ROLE_LABEL: Record<string, string> = { parent: '학부모', student: '학생', owner: '관리자' };

  const formatModifiedAt = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return iso; }
  };

  const hasChanges = JSON.stringify(schedules) !== JSON.stringify(savedSchedules);

  const handleChildChange = (event: any) => {
    const newId = event.target.value as string;
    setSelectedChildId(newId);
    navigate(`/parent/period-schedule/${newId}`);
  };

  if (children.length === 0 && !isLoading) {
    return (
      <Box>
        <Alert severity="info">
          등록된 자녀가 없습니다. 독서실에 문의하여 자녀를 연결해주세요.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CalendarMonthIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          교시 스케줄 등록
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        자녀의 요일별 수강 교시를 체크하여 등록합니다. 셀을 터치하거나 드래그하세요.
      </Typography>

      {/* 자녀 선택 */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ maxWidth: 300 }}>
          <InputLabel>자녀 선택</InputLabel>
          <Select value={selectedChildId} label="자녀 선택" onChange={handleChildChange}>
            {children.map((child) => (
              <MenuItem key={child.id} value={child.id}>
                {child.name} - {child.organizationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Alert severity="warning">
          독서실에서 교시 시간표를 아직 설정하지 않았습니다. 관리자에게 문의해주세요.
        </Alert>
      ) : (
        <>
          {/* 교시 그리드 */}
          <Paper sx={{ p: 0, mb: 3, overflow: 'auto' }}>
            <Box
              onTouchMove={handleTouchMove}
              sx={{
                display: 'grid',
                gridTemplateColumns: `minmax(100px, 140px) repeat(7, 1fr)`,
                minWidth: 500,
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              {/* 헤더 행 */}
              <Box sx={{ p: 1, fontWeight: 700, backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd', display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight={700}>교시</Typography>
              </Box>
              {DAYS.map((day, idx) => (
                <Box key={idx} sx={{ p: 1, fontWeight: 700, backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd', textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={700}>{day}</Typography>
                </Box>
              ))}

              {/* 교시 행 (휴식 제외) */}
              {templates.filter((t) => t.period_type !== 'break').map((template) => {
                const isBreak = false;

                return (
                  <React.Fragment key={template.period_number}>
                    {/* 교시 이름 셀 */}
                    <Box
                      sx={{
                        p: 1, borderBottom: '1px solid #eee',
                        backgroundColor: isBreak ? '#fff8e1' : '#fff',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        minHeight: 48,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {template.period_name}
                        </Typography>
                        {isBreak && <Chip label="휴식" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                        {template.start_time}~{template.end_time}
                      </Typography>
                    </Box>

                    {/* 요일별 셀 */}
                    {DAYS.map((_, dayIdx) => {
                      const isInCurrent = (schedules[String(dayIdx)] || []).includes(template.period_number);
                      const isInSaved = (savedSchedules[String(dayIdx)] || []).includes(template.period_number);
                      const isPendingDelete = !isInCurrent && isInSaved;

                      let cellBg = '#fafafa';
                      if (isBreak) cellBg = '#fff8e1';
                      else if (isInCurrent && isInSaved) cellBg = '#2196f3';
                      else if (isInCurrent && !isInSaved) cellBg = '#4caf50';
                      else if (isPendingDelete) cellBg = '#ffebee';

                      return (
                        <Box
                          key={dayIdx}
                          data-cell={isBreak ? undefined : `${dayIdx}-${template.period_number}`}
                          onMouseDown={isBreak ? undefined : () => startDrag(dayIdx, template.period_number)}
                          onMouseEnter={isBreak ? undefined : () => continueDrag(dayIdx, template.period_number)}
                          onTouchStart={isBreak ? undefined : () => startDrag(dayIdx, template.period_number)}
                          sx={{
                            borderBottom: '1px solid #eee',
                            borderRight: '1px solid #f0f0f0',
                            backgroundColor: cellBg,
                            border: isPendingDelete ? '2px solid #f44336' : undefined,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            minHeight: 48,
                            cursor: isBreak ? 'default' : 'pointer',
                            transition: 'background-color 0.1s',
                            '&:hover': isBreak ? {} : { opacity: 0.85 },
                            '&:active': isBreak ? {} : { transform: 'scale(0.95)' },
                          }}
                        >
                          {isBreak ? (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          ) : isInCurrent ? (
                            <CheckIcon sx={{ color: 'white', fontSize: 22 }} />
                          ) : isPendingDelete ? (
                            <CloseIcon sx={{ color: '#f44336', fontSize: 18 }} />
                          ) : null}
                        </Box>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </Box>
          </Paper>

          {/* 범례 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 16, borderRadius: '3px', backgroundColor: '#2196f3' }} />
              <Typography variant="caption" color="text.secondary">저장됨</Typography>
            </Box>
            {hasChanges && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '3px', backgroundColor: '#4caf50' }} />
                  <Typography variant="caption" color="text.secondary">새로 추가</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, borderRadius: '3px', backgroundColor: '#ffebee', border: '2px solid #f44336' }} />
                  <Typography variant="caption" color="text.secondary">삭제 예정</Typography>
                </Box>
              </>
            )}
          </Box>

          {/* 수정자 정보 */}
          {modifierInfo && modifierInfo.updated_at && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              마지막 수정: {ROLE_LABEL[modifierInfo.modifier_role] || modifierInfo.modifier_role} | {formatModifiedAt(modifierInfo.updated_at)}
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving} size="large">
              {isSaving ? '저장 중...' : hasChanges ? '변경사항 저장' : '스케줄 저장'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
